/*
 * avlx-main-product: mobile behaviours for sections/main-product.liquid.
 * - mobile-media-sticky: toggles .is-stuck (pinned shadow) on the gallery card and releases it
 *   once the add-to-cart card scrolls above it.
 * - thumbs-side: keeps the selected thumbnail's neighbours in view and sets the strip's fades.
 * Every listener and observer is tied to the element, so a Theme Editor re-render does not stack them.
 */
{
  const MOBILE_QUERY = '(max-width: 699px)';

  class AvlxMainProduct extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (!this.section) return;
      this.controller = new AbortController();
      this.observers = [];
      this.frames = new Set();
      this.mq = window.matchMedia(MOBILE_QUERY);
      if (this.hasAttribute('mobile-media-sticky')) this.initSticky();
      if (this.hasAttribute('thumbs-side')) this.initThumbs();
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.observers.forEach((observer) => observer.disconnect());
      this.frames.forEach((id) => cancelAnimationFrame(id));
      this.controller = null;
    }

    nextFrame(callback) {
      const id = requestAnimationFrame(() => {
        this.frames.delete(id);
        callback();
      });
      this.frames.add(id);
    }

    initSticky() {
      const { section, mq } = this;
      const { signal } = this.controller;
      let ticking = false;
      let listening = false;
      let inView = false;
      let stickyTop = null;

      const currentShift = (el) => {
        const m = /translateY\((-?[\d.]+)px\)/.exec(el.style.transform || '');
        return m ? -parseFloat(m[1]) : 0;
      };

      const update = () => {
        ticking = false;
        const el = section.querySelector('.product > safe-sticky:not(.product-info)');
        if (!el) return;
        if (!mq.matches) {
          el.classList.remove('is-stuck');
          el.style.removeProperty('transform');
          return;
        }
        // Read the pinned offset once and again only after the viewport or the header height changes.
        if (stickyTop === null) stickyTop = parseFloat(getComputedStyle(el).top) || 0;
        const shift = currentShift(el);
        const rect = el.getBoundingClientRect();
        const pinnedTop = rect.top + shift;
        const pinnedBottom = rect.bottom + shift;
        // Release: once the add-to-cart card has scrolled above the pinned card, move the card up
        // with the page so it scrolls away naturally (and slides back when scrolling up again).
        const atc = section.querySelector('.product-info .avlx-atc-card, .product-info buy-buttons');
        const release = atc ? Math.max(0, pinnedBottom - atc.getBoundingClientRect().bottom) : 0;
        el.style.transform = release > 0 ? `translateY(${-Math.round(release)}px)` : '';
        el.classList.toggle('is-stuck', pinnedTop <= stickyTop + 1 && window.scrollY > 0 && release < rect.height);
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        this.nextFrame(update);
      };

      // The release offset follows the scroll position pixel by pixel, so it needs a scroll listener;
      // the observer limits that listener to phone widths while the section is on screen.
      const sync = () => {
        const shouldListen = inView && mq.matches;
        if (shouldListen && !listening) window.addEventListener('scroll', onScroll, { passive: true, signal });
        if (!shouldListen && listening) window.removeEventListener('scroll', onScroll);
        listening = shouldListen;
        update();
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          inView = entries[entries.length - 1].isIntersecting;
          sync();
        });
        observer.observe(section);
        this.observers.push(observer);
      } else {
        inView = true;
      }

      mq.addEventListener('change', () => {
        stickyTop = null;
        sync();
      }, { signal });
      window.addEventListener('resize', () => {
        stickyTop = null;
      }, { passive: true, signal });
      // height-observer rewrites --header-height and --announcement-bar-height on <html> when they change.
      const rootObserver = new MutationObserver(() => {
        stickyTop = null;
      });
      rootObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      this.observers.push(rootObserver);
      sync();
    }

    initThumbs() {
      const { section, mq } = this;
      const { signal } = this.controller;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const parts = () => {
        const gallery = section.querySelector('.product-gallery--avlx-thumbs-side');
        const list = gallery && gallery.querySelector('.product-gallery__thumbnail-list');
        return list ? { gallery, list } : null;
      };

      const updateFades = () => {
        const p = parts();
        if (!p) return;
        const { gallery, list } = p;
        const more = [];
        if (mq.matches && list.scrollTop > 2) more.push('above');
        if (mq.matches && list.scrollTop + list.clientHeight < list.scrollHeight - 2) more.push('below');
        gallery.setAttribute('data-avlx-more', more.join(' '));
      };

      const visibleSibling = (el, dir) => {
        let s = el[dir];
        while (s && s.hidden) s = s[dir];
        return s;
      };

      const reveal = (index) => {
        const p = parts();
        if (!p || !mq.matches) return;
        const { list } = p;
        const current = list.children[index];
        if (!current || current.hidden) return;
        const first = visibleSibling(current, 'previousElementSibling') || current;
        const last = visibleSibling(current, 'nextElementSibling') || current;
        const view = list.clientHeight;
        let target = list.scrollTop;
        const bottom = last.offsetTop + last.offsetHeight + 1;
        if (bottom > target + view) target = bottom - view;
        if (first.offsetTop - 1 < target) target = first.offsetTop - 1;
        target = Math.max(0, Math.min(target, list.scrollHeight - view));
        list.scrollTo({ top: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };

      section.addEventListener('carousel:change', (event) => {
        if (!event.target.closest('.product-gallery--avlx-thumbs-side')) return;
        this.nextFrame(() => reveal(event.detail.index));
      }, { signal });
      section.addEventListener('click', (event) => {
        const thumb = event.target.closest('.product-gallery--avlx-thumbs-side .product-gallery__thumbnail');
        if (thumb) this.nextFrame(() => reveal([...thumb.parentElement.children].indexOf(thumb)));
      }, { signal });
      section.addEventListener('scroll', (event) => {
        if (event.target.classList && event.target.classList.contains('product-gallery__thumbnail-list')) updateFades();
      }, { capture: true, passive: true, signal });
      if ('ResizeObserver' in window) {
        const p = parts();
        if (p) {
          const observer = new ResizeObserver(updateFades);
          observer.observe(p.list);
          this.observers.push(observer);
        }
      }
      mq.addEventListener('change', updateFades, { signal });
      updateFades();
    }
  }

  if (!window.customElements.get('avlx-main-product')) {
    window.customElements.define('avlx-main-product', AvlxMainProduct);
  }
}
